// Composer Studio — 캔버스 인라인 텍스트 에디터 (오버레이)
//
// 캔버스에서 텍스트 노드/정적 셀을 더블클릭하면 그 자리에 절대배치 <textarea>를 띄워
// WYSIWYG으로 텍스트를 직접 편집한다. 대상 요소의 캔버스 기준 rect를 측정해 그 위에 겹친다
// (DragLayer의 getNodeRect 패턴과 동일). 대상 요소의 computed style(폰트/정렬)을 복사해
// 화면 표시와 유사하게 보이도록 한다.
//
//   - Enter(Shift 없이) = 커밋, Shift+Enter = 줄바꿈, Esc = 취소, blur = 커밋
//   - 마운트 시 focus + 전체 select
//   - 대상 요소가 트리에서 사라지면(없으면) 에디터를 닫는다(가드).

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Rect } from './canvasRect';
import { cssEscape, rectRelativeTo } from './canvasRect';

/** 편집 대상 — 텍스트 노드 또는 표의 정적 셀 */
export type EditingTarget =
  | { kind: 'node'; id: string }
  | { kind: 'cell'; tableId: string; row: number; col: number };

/**
 * 편집 종료 출처.
 *   - keyboard: Enter(커밋)/Esc(취소)/Tab 등 키보드로 종료 → 컨테이너 포커스 복원 대상.
 *   - blur:     캔버스 밖 다른 곳 클릭으로 종료 → 사용자가 클릭한 곳 포커스 유지(복원 X).
 *   - auto:     대상 요소 소멸 등 프로그램적 종료 → 복원 X.
 */
export type CloseReason = 'keyboard' | 'blur' | 'auto';

/**
 * computed verticalAlign → flex justifyContent 매핑.
 * 셀(td)은 보통 top/middle/bottom. 상단류(top/baseline/sub/super/text-top)는 flex-start,
 * middle은 center, 하단류(bottom/text-bottom)는 flex-end. 모르는 값은 flex-start.
 */
export function justifyForVerticalAlign(va: string | undefined): React.CSSProperties['justifyContent'] {
  switch (va) {
    case 'middle':
      return 'center';
    case 'bottom':
    case 'text-bottom':
      return 'flex-end';
    default:
      return 'flex-start';
  }
}

/** 편집 대상의 DOM selector (data-composer-node-id 또는 data-composer-cell). */
function selectorFor(target: EditingTarget): string {
  if (target.kind === 'node') {
    return `[data-composer-node-id="${cssEscape(target.id)}"]`;
  }
  return `[data-composer-cell="${cssEscape(`${target.tableId}::${target.row}::${target.col}`)}"]`;
}

/** 편집 대상 요소의 캔버스 기준 상대 rect (없으면 null). */
function getTargetRect(canvas: HTMLElement, target: EditingTarget): Rect | null {
  const el = canvas.querySelector<HTMLElement>(selectorFor(target));
  return el ? rectRelativeTo(canvas, el) : null;
}

/**
 * 인라인 텍스트 에디터 오버레이.
 *
 * editingTarget이 있을 때만 마운트된다. 대상 요소 위에 textarea를 겹쳐 렌더하고,
 * 커밋(onCommit) 시 새 텍스트를, 취소(onCancel) 시 변경 없이 닫힌다. 대상이 사라지면
 * 자동으로 onCancel을 호출해 닫는다.
 */
export function InlineTextEditor({
  canvas,
  target,
  zoom = 1,
  initialText,
  selectAllOnMount = true,
  onCommit,
  onSaveShortcut,
  onCancel,
  onPreview,
  onTab,
}: {
  canvas: HTMLElement | null;
  target: EditingTarget;
  /** 캔버스 줌 배율(미지정=1). textarea를 scale해 폰트/크기를 스케일된 노드와 맞춘다. */
  zoom?: number;
  /** 편집 시작 시점의 현재 텍스트(노드 text 또는 셀 text). */
  initialText: string;
  /**
   * 마운트 시 전체 선택 여부(기본 true). 더블클릭/F2/Enter 진입은 전체 선택(true).
   * 엑셀식 자동 타이핑 진입(한 글자로 시작)은 false — select()하면 그 글자가 다음 입력에
   * 대체되므로 커서를 끝에만 둔다.
   */
  selectAllOnMount?: boolean;
  /** 커밋 — 새 텍스트 값으로 적용 후 닫기. reason은 종료 출처(포커스 복원 분기용). */
  onCommit: (value: string, reason: CloseReason) => void;
  /**
   * 편집 중 Ctrl/Cmd+S — 현재 값을 먼저 커밋한 뒤 앱 저장을 트리거한다. 편집 중에는 이 에디터가
   * keydown을 stopPropagation하므로 window의 저장 단축키가 도달하지 못해(브라우저 "페이지 저장"
   * 대화상자가 뜸) 여기서 직접 처리한다. EditorPage가 지연 저장 래퍼(방금 커밋한 값 반영)를 넘긴다.
   */
  onSaveShortcut?: () => void;
  /** 취소 — 변경 없이 닫기. reason은 종료 출처(포커스 복원 분기용). */
  onCancel: (reason: CloseReason) => void;
  /** 입력 live 반영 — 매 입력마다 현재 값 통지(히스토리 미기록 present-only 갱신용). */
  onPreview?: (value: string) => void;
  /** Tab — 셀 편집 중 다음/이전 셀로 이동(셀 대상일 때만 호출). 현재 값 커밋 후 호출된다. */
  onTab?: (dir: 'next' | 'prev') => void;
}) {
  const [rect, setRect] = useState<Rect | null>(() =>
    canvas ? getTargetRect(canvas, target) : null,
  );
  // 대상 요소의 computed style(WYSIWYG). 폰트/정렬을 textarea에 복사한다.
  const [fontStyle, setFontStyle] = useState<{
    fontFamily?: string;
    fontSize?: string;
    fontWeight?: string;
    fontStyle?: string;
    textDecoration?: string;
    textAlign?: string;
    verticalAlign?: string;
    color?: string;
    lineHeight?: string;
    padding?: string;
  }>({});
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // 커밋이 중복 호출되지 않도록(예: Enter 후 blur) 1회만 닫히게 가드.
  const closedRef = useRef(false);

  // 대상 rect + computed style 측정(마운트/대상 변경 시). 한 프레임 뒤 재측정해 레이아웃 반영.
  useLayoutEffect(() => {
    if (!canvas) return;
    let retryRaf = 0;
    // 대상이 편집 진입과 **같은 커밋에 생성**되는 경우(추가 직후 자동 편집), 캔버스가 다중 패스
    // 렌더(페이지네이션 재배치 등)면 첫 측정 시점엔 아직 DOM에 없다 — 즉시 닫으면 편집 진입이
    // 소리 없이 무효가 되므로(2026-07-15 실사고), 짧은 유예 동안 rAF 재시도 후에만 소멸로 판정.
    const graceUntil = performance.now() + 300;
    // 대상 el 관찰은 최초 발견 시점에 시작(지연 발견 포함) — 입력 live 반영으로 커지거나
    // 위쪽 행이 커져 위치가 밀리면 재측정해 textarea가 대상을 따라가게 한다.
    let observedEl: HTMLElement | null = null;
    const ro = new ResizeObserver(() => measure());
    function measure() {
      const el = canvas!.querySelector<HTMLElement>(selectorFor(target));
      if (!el) {
        if (performance.now() < graceUntil) {
          retryRaf = requestAnimationFrame(measure);
          return;
        }
        // 유예 후에도 없음 = 대상이 사라짐 → 닫기(프로그램적 종료라 포커스 복원 X).
        if (!closedRef.current) {
          closedRef.current = true;
          onCancel('auto');
        }
        return;
      }
      if (observedEl !== el) {
        if (observedEl) ro.unobserve(observedEl);
        ro.observe(el);
        observedEl = el;
      }
      setRect(rectRelativeTo(canvas!, el));
      const cs = window.getComputedStyle(el);
      setFontStyle({
        fontFamily: cs.fontFamily,
        fontSize: cs.fontSize,
        fontWeight: cs.fontWeight,
        fontStyle: cs.fontStyle,
        textDecoration: cs.textDecorationLine || undefined,
        textAlign: cs.textAlign,
        verticalAlign: cs.verticalAlign,
        color: cs.color,
        lineHeight: cs.lineHeight,
        padding: cs.padding,
      });
    }
    measure();
    const raf = requestAnimationFrame(measure);
    ro.observe(canvas);
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(retryRaf);
      ro.disconnect();
    };
    // canvas/target만 의존(onCancel은 안정 참조 가정 — 미포함해 재측정 루프 방지).
  }, [canvas, target]);

  // textarea를 콘텐츠 높이로 맞춘다(auto-grow). textarea는 height:auto로 두면 콘텐츠에 맞춰
  // 자동 성장하지 않으므로(rows 기반 고정), scrollHeight를 직접 대입한다. wrapper는 셀 높이로
  // 고정되어 justifyContent로 수직 배치하므로, textarea에는 셀 높이 하한(floor)을 주지 않는다
  // (floor를 주면 textarea가 셀을 꽉 채워 수직 정렬 효과가 사라진다).
  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // 첫 렌더 가능 시점에 focus + (옵션)전체 select + 초기 auto-grow.
  // selectAllOnMount=false(자동 타이핑 진입)면 select()를 생략 — 커서는 기본 끝에 두어
  // 이어지는 입력이 첫 글자를 대체하지 않게 한다.
  // deps에 rect: 대상 노드가 편집 진입과 **같은 커밋에 생성**되는 경우(추가 직후 자동 편집)
  // 마운트 시점엔 rect=null이라 textarea가 아직 없다 — 측정 후 textarea가 처음 생길 때 1회
  // 수행한다(ref 가드). 기존 더블클릭 진입(rect 즉시 확보)은 마운트 시 1회로 동작 동일.
  const focusedOnceRef = useRef(false);
  useEffect(() => {
    if (focusedOnceRef.current) return;
    const el = textareaRef.current;
    if (!el) return;
    focusedOnceRef.current = true;
    autoGrow();
    el.focus();
    if (selectAllOnMount) el.select();
  }, [rect, selectAllOnMount]);

  const commit = (reason: CloseReason) => {
    if (closedRef.current) return;
    closedRef.current = true;
    onCommit(textareaRef.current?.value ?? initialText, reason);
  };
  const cancel = (reason: CloseReason) => {
    if (closedRef.current) return;
    closedRef.current = true;
    onCancel(reason);
  };

  if (!canvas || !rect) return null;

  return (
    // wrapper — 위치/크기/zoom·선택 테두리·배경을 담당하고, flex로 textarea를 수직 배치한다.
    // textarea 자체는 vertical-align을 지원하지 않으므로, 콘텐츠 높이의 textarea를 셀 높이의
    // wrapper 안에서 justifyContent(수직 정렬 매핑)로 위/중앙/아래에 배치한다.
    <div
      style={{
        position: 'absolute',
        top: rect.top,
        left: rect.left,
        // rect는 스케일된 화면 px. 폰트는 미스케일(논리) 값을 복사하므로, wrapper를 논리 크기로
        // 두고 transform:scale(zoom)으로 키워 폰트·크기를 스케일된 노드와 정확히 맞춘다(WYSIWYG).
        width: rect.width / zoom,
        height: rect.height / zoom,
        transform: zoom === 1 ? undefined : `scale(${zoom})`,
        transformOrigin: 'top left',
        margin: 0,
        boxSizing: 'border-box',
        // 테두리는 inset box-shadow로(레이아웃 영향 0 → 원래 셀과 동일 높이 유지).
        boxShadow: 'inset 0 0 0 2px #4f46e5', // brand-600
        overflow: 'hidden',
        background: '#ffffff',
        zIndex: 40,
        display: 'flex',
        flexDirection: 'column',
        // 수직 정렬 — 셀 computed verticalAlign을 flex justifyContent로 매핑.
        justifyContent: justifyForVerticalAlign(fontStyle.verticalAlign),
      }}
    >
      <textarea
        ref={textareaRef}
        defaultValue={initialText}
        // 기본 rows=2면 height:auto/scrollHeight 최소가 2줄이 되어 한 줄 입력에도 셀이 한 칸 더 커진다.
        // rows=1로 최소를 한 줄로 — auto-grow(scrollHeight)는 그대로 적용.
        rows={1}
        onChange={(e) => {
          // 입력 즉시 자체 auto-grow(콘텐츠 높이). scrollHeight는 미스케일 논리값이라 height에
          // 그대로 대입 OK. 콘텐츠가 셀보다 커지면 live 반영→셀 성장→ResizeObserver 재측정으로
          // wrapper 높이가 뒤이어 따라간다. 셀이 콘텐츠보다 크면 textarea는 콘텐츠 높이로 두고
          // wrapper가 justifyContent로 수직 배치한다(floor 미적용 — 수직 정렬 효과 보존).
          autoGrow();
          onPreview?.(e.currentTarget.value);
        }}
        onKeyDown={(e) => {
          // 키 이벤트가 캔버스 단축키 핸들러로 새지 않게 차단(가드는 있으나 안전).
          e.stopPropagation();
          // Ctrl/Cmd+S — 현재 값 커밋 후 저장(브라우저 "페이지 저장" 대화상자 차단). stopPropagation을
          // 유지하므로 window 저장 핸들러엔 도달하지 않는다 → 여기서 직접 커밋+저장을 호출한다.
          if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 's') {
            e.preventDefault();
            commit('keyboard');
            onSaveShortcut?.();
            return;
          }
          // Ctrl/Cmd+D — 편집 중 노드 복제는 의미 없으므로 삼킨다(브라우저 북마크 대화상자 차단).
          if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'd') {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            commit('keyboard');
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel('keyboard');
          } else if (e.key === 'Tab' && target.kind === 'cell' && onTab) {
            // 셀 편집 중 Tab → 현재 값 커밋 후 다음/이전 셀로(Shift+Tab=이전).
            // commit이 closedRef를 true로 만들기 전 현재 값을 적용하고, onTab은 그 직후 호출.
            // (Tab 후 포커스는 handleInlineTab이 별도로 복원하므로 keyboard 복원 중복은 무해.)
            e.preventDefault();
            commit('keyboard');
            onTab(e.shiftKey ? 'prev' : 'next');
          }
          // Shift+Enter는 textarea 기본 줄바꿈. (node 대상이거나 onTab 미제공 시 Tab은 기본 동작.)
        }}
        onBlur={() => commit('blur')}
        // 캔버스 클릭 위임(선택)으로 전파되지 않게.
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label="인라인 텍스트 편집"
        style={{
          width: '100%',
          // 콘텐츠 높이(auto-grow). wrapper가 셀 높이를 갖고 수직 배치를 담당한다.
          height: 'auto',
          margin: 0,
          boxSizing: 'border-box',
          // 위치/테두리/배경은 wrapper가 담당 — textarea 자신은 투명·무테두리.
          border: 'none',
          outline: 'none',
          resize: 'none',
          overflow: 'hidden',
          background: 'transparent',
          // 캔버스 콘텐츠는 user-select:none이지만, 편집 중 textarea 내부 드래그 선택은 허용.
          userSelect: 'text',
          // WYSIWYG — 대상 요소의 폰트/수평 정렬 복사.
          fontFamily: fontStyle.fontFamily,
          fontSize: fontStyle.fontSize,
          fontWeight: fontStyle.fontWeight,
          fontStyle: fontStyle.fontStyle,
          textDecoration: fontStyle.textDecoration,
          textAlign: fontStyle.textAlign as React.CSSProperties['textAlign'],
          color: fontStyle.color,
          lineHeight: fontStyle.lineHeight,
          padding: fontStyle.padding,
        }}
      />
    </div>
  );
}
